/* OPFS — the origin private file system. Where the crown jewels live.
 *
 * Photos and audio are written here as original bytes and never re-encoded
 * before upload. They are not pruned locally until the server has HEADed the
 * object and confirmed receipt. Losing a queued file is the one unrecoverable
 * failure this app has, so every path here fails loud.
 *
 * Layout:
 *   /media/{uuid}.jpg|.m4a
 *   /tiles/{property}.pmtiles
 */

let rootPromise: Promise<FileSystemDirectoryHandle> | null = null;

function root(): Promise<FileSystemDirectoryHandle> {
  if (!rootPromise) rootPromise = navigator.storage.getDirectory();
  return rootPromise;
}

export const opfsSupported = (): boolean =>
  typeof navigator !== 'undefined' && !!navigator.storage?.getDirectory;

async function dir(name: string): Promise<FileSystemDirectoryHandle> {
  return (await root()).getDirectoryHandle(name, { create: true });
}

export async function writeMedia(filename: string, blob: Blob): Promise<void> {
  const media = await dir('media');
  const handle = await media.getFileHandle(filename, { create: true });
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

export async function readMedia(filename: string): Promise<File | null> {
  try {
    const media = await dir('media');
    const handle = await media.getFileHandle(filename);
    return await handle.getFile();
  } catch {
    return null;
  }
}

export async function deleteMedia(filename: string): Promise<void> {
  try {
    const media = await dir('media');
    await media.removeEntry(filename);
  } catch {
    /* already gone — deleting media is idempotent by design */
  }
}

/** Object URLs are revoked by the caller. Returns null if the file vanished. */
export async function mediaUrl(filename: string): Promise<string | null> {
  const file = await readMedia(filename);
  return file ? URL.createObjectURL(file) : null;
}

/* ── Tiles ───────────────────────────────────────────────────────────────── */

export async function writeTilePack(
  property: string,
  stream: ReadableStream<Uint8Array>,
  onProgress?: (bytes: number) => void,
): Promise<void> {
  const tiles = await dir('tiles');
  const handle = await tiles.getFileHandle(`${property}.pmtiles`, { create: true });
  const writable = await handle.createWritable();
  const reader = stream.getReader();
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    await writable.write(value as unknown as ArrayBufferView<ArrayBuffer>);
    total += value.byteLength;
    onProgress?.(total);
  }
  await writable.close();
}

export async function tilePack(property: string): Promise<File | null> {
  try {
    const tiles = await dir('tiles');
    const handle = await tiles.getFileHandle(`${property}.pmtiles`);
    return await handle.getFile();
  } catch {
    return null;
  }
}

export async function deleteTilePack(property: string): Promise<void> {
  try {
    const tiles = await dir('tiles');
    await tiles.removeEntry(`${property}.pmtiles`);
  } catch {
    /* not downloaded */
  }
}

/* ── Persistence ─────────────────────────────────────────────────────────── */

export interface StorageReport {
  persisted: boolean;
  usageBytes: number;
  quotaBytes: number;
}

/**
 * Requested on every launch, not once. Safari drops the grant, and an
 * un-persisted origin is one storage-pressure event away from losing a day's
 * work. Cheap to ask; catastrophic to skip.
 */
export async function ensurePersisted(): Promise<StorageReport> {
  let persisted = false;
  try {
    persisted = (await navigator.storage?.persisted?.()) ?? false;
    if (!persisted) persisted = (await navigator.storage?.persist?.()) ?? false;
  } catch {
    persisted = false;
  }

  let usageBytes = 0;
  let quotaBytes = 0;
  try {
    const est = await navigator.storage?.estimate?.();
    usageBytes = est?.usage ?? 0;
    quotaBytes = est?.quota ?? 0;
  } catch {
    /* estimate unsupported — report zeroes rather than guess */
  }

  return { persisted, usageBytes, quotaBytes };
}

export { formatBytes } from '@kilroy/shared';
